import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'

import { Divider } from '@gto/ui/divider'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { Typography } from '@gto/ui/typography'

import { useAccountRetrieve } from '@/api/gen'
import { ChangeEmail } from '@/pages/account/components/change-email'
import { ChangePassword } from '@/pages/account/components/change-password'
import { DeleteAccountButton } from '@/pages/account/components/delete-account-button'

const routeApi = getRouteApi('/_layout/_private/app/user/account')

export type AccountFormType = 'email' | 'password' | 'profile' | null

type FormConfig = {
  titleId: string
}

const FORM_CONFIGS: Record<Exclude<AccountFormType, null>, FormConfig> = {
  email: { titleId: 'common.controls.changeEmail' },
  password: { titleId: 'common.controls.changePassword' },
  profile: { titleId: 'user.settings.editProfile' },
}

export const AccountRoute = () => {
  const search = routeApi.useSearch()
  const isSmAndLower = useMediaQuery('down', 'sm')
  const [activeForm, setActiveForm] = useState<AccountFormType>(null)

  // Fetch account data once for all widgets
  const { data: accountData, isPending: isLoadingAccount } = useAccountRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
    },
  })

  // Auto-open email form when token is present in URL
  useEffect(
    function autoOpenEmailFormFromUrlToken() {
      if (search.token !== undefined) {
        setActiveForm('email')
      }
    },
    [search.token],
  )

  const openForm = (formType: AccountFormType) => {
    setActiveForm(formType)
  }

  const closeForm = () => {
    setActiveForm(null)
  }

  const formConfig = activeForm === null ? null : FORM_CONFIGS[activeForm]

  // Hide header when token is present (validation screens have their own headers)
  const hasToken = search.token !== undefined

  return (
    <>
      {hasToken ? null : (
        <div className="flex items-center justify-between">
          <Typography
            component="h1"
            variant={isSmAndLower ? 'h5' : 'h4'}
          >
            <FormattedMessage
              id={
                formConfig === null
                  ? 'user.settings.accountManagement'
                  : (formConfig.titleId as
                      | 'common.controls.changeEmail'
                      | 'common.controls.changePassword'
                      | 'user.settings.editProfile')
              }
            />
          </Typography>
          {activeForm === null ? (
            <div className="flex max-w-[200px] sm:hidden">
              <DeleteAccountButton />
            </div>
          ) : null}
        </div>
      )}
      <div className="flex gap-6 sm:flex-col">
        <div
          className="flex w-full flex-col gap-8"
          style={{ maxWidth: hasToken ? 'unset' : '550px' }}
        >
          {activeForm === null || activeForm === 'email' ? (
            <ChangeEmail
              accountData={accountData}
              isActive={activeForm === 'email'}
              isPending={isLoadingAccount}
              onClose={closeForm}
              onOpen={() => openForm('email')}
            />
          ) : null}
          {activeForm === null || activeForm === 'password' ? (
            <ChangePassword
              isActive={activeForm === 'password'}
              isPending={isLoadingAccount}
              onClose={closeForm}
              onOpen={() => openForm('password')}
            />
          ) : null}
          {activeForm === null ? (
            <>
              <Divider className="hidden sm:flex" />
              <div className="hidden max-w-[200px] self-end sm:flex sm:max-w-none sm:self-stretch">
                <DeleteAccountButton />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  )
}
